import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import Modal from "../../../shared/components/ModalAprobar";

const ModalAlertContextAprobar = createContext(null);

export const useModalAlertaAprobar = () => {
  const context = useContext(ModalAlertContextAprobar);
  if (!context) {
    throw new Error("useModalAlert must be used within a ModalAlertProvider");
  }
  return context;
};

const initialAlertState = {
  isOpen: false,
  message: "",
  type: "primary",
  onConfirm: () => {},
  onCancel: () => {},
  showCancel: true,
  showInput: false,
  inputPlaceholder: "",
  onInputChange: () => {},
};

export const ModalAlertProviderAprobar = ({ children }) => {
  const [modalState, setModalState] = useState(initialAlertState);

  // Estado separado para el valor del input
  const [inputValue, setInputValue] = useState("");
  // Ref para poder leer siempre el valor actual desde callbacks almacenados en modalState
  const inputValueRef = useRef(inputValue);
  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  const handleClose = useCallback(() => {
    setModalState((s) => ({ ...s, isOpen: false }));
    setInputValue("");
  }, []);

  const handleConfirm = useCallback(() => {
    if (modalState.onConfirm) {
      // Pasamos el valor actual del input separado
      modalState.onConfirm(inputValueRef.current);
    }
  }, [modalState.onConfirm]);

  const handleCancel = useCallback(() => {
    if (modalState.onCancel) {
      modalState.onCancel();
    }
  }, [modalState.onCancel]);

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setInputValue(value);
      if (modalState.onInputChange) {
        modalState.onInputChange(value);
      }
    },
    [modalState.onInputChange]
  );

  const showDecisionModal = useCallback(
    (type, title, onApprove, onReject) => {
      const showInputModal = () => {
        // Reiniciamos inputValue cuando mostramos el modal de input
        setInputValue("");
        setModalState((s) => ({
          ...s,
          isOpen: true,
          type: "warning",
          title: "Motivo del Rechazo",
          message: "Ingrese el motivo del rechazo:",
          showCancel: true,
          showInput: true,
          // Cuando se ejecute, leerá el valor actual desde inputValueRef
          onConfirm: () => {
            onReject(inputValueRef.current);
            handleClose();
          },
          onCancel: handleClose,
        }));
      };

      const showRejectConfirmation = () => {
        setModalState({
          isOpen: true,
          type: "warning",
          title: "Confirmación de Rechazo",
          message: "¿Está seguro que desea rechazar esta versión?",
          showCancel: true,
          onConfirm: showInputModal,
          onCancel: handleClose,
          showInput: false,
        });
      };

      const showApproveConfirmation = () => {
        setModalState({
          isOpen: true,
          type: "warning",
          title: "Confirmación de Aprobación",
          message: "¿Está seguro que desea aprobar esta versión?",
          showCancel: true,
          onConfirm: () => {
            onApprove();
            handleClose();
          },
          onCancel: handleClose,
          showInput: false,
        });
      };

      setModalState({
        isOpen: true,
        type,
        title: title,
        message: "¿Desea aprobar esta versión?",
        showCancel: true,
        onConfirm: showApproveConfirmation,
        onCancel: showRejectConfirmation,
        showInput: false,
      });
    },
    [handleClose]
  );

  const contextValue = useMemo(
    () => ({
      showDecisionModal,
    }),
    [showDecisionModal]
  );

  return (
    <ModalAlertContextAprobar.Provider value={contextValue}>
      {children}
      <Modal
        color={modalState.type}
        title={modalState.message}
        modal={modalState.isOpen}
        onCancel={handleCancel}
        onOk={handleConfirm}
        toggle={handleClose}
        showCancel={modalState.showCancel}
        showInput={modalState.showInput}
        inputValue={inputValue}
        onInputChange={handleInputChange}
      />
    </ModalAlertContextAprobar.Provider>
  );
};
